import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";
import hospitalInfo from "../constants/hospitalInfo";
import formatDate from "./formatDate";
import logoBase64 from "../constants/logoBase64";
import { translateDietToTamil, hasTamil } from "./tamilTranslate";

/**
 * Builds standard vector PDF document for English diet chart using the Green Naturopathy Bill Template design.
 */
export function buildDietPDFDoc(diet = {}, patient = {}) {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 40;

    // Palette matching the Bill Template
    const brandGreen = [75, 128, 38];      // #4b8026 Organic Leaf Green
    const grayHeaderBg = [209, 213, 219];  // #d1d5db Header gray
    const grayContentBg = [249, 250, 251]; // #f9fafb Soft card gray
    const grayBorder = [209, 213, 219];    // #d1d5db Box border
    const textDark = [31, 41, 55];         // #1f2937 Charcoal
    const textMuted = [107, 114, 128];     // #6b7280 Muted gray

    // ---------- Top Header: Title on Left, Logo on Right ----------
    doc.setFont("times", "bold");
    doc.setFontSize(32);
    doc.setTextColor(...brandGreen);
    doc.text("Diet & Nutrition Chart", marginX, 52);

    // Official Logo on top right (exact content size: 4.5cm x 2.26cm -> 127.56pt x 64.06pt)
    const logoWidth = 127.56;
    const logoHeight = 64.06;
    const logoX = pageWidth - marginX - logoWidth;
    const logoY = 18;
    if (logoBase64) {
        try {
            doc.addImage(logoBase64, "PNG", logoX, logoY, logoWidth, logoHeight);
        } catch (e) {
            console.warn("Could not render logo in Diet PDF", e);
        }
    }

    let y = 92;

    // ---------- Patient & Prescription Info Cards ----------
    const cardGap = 14;
    const cardWidth = (pageWidth - (marginX * 2) - cardGap) / 2;
    const isExplicitEmptyPatient = patient === null || patient === false || (patient && typeof patient === 'object' && Object.keys(patient).length === 0);
    const patientObj = isExplicitEmptyPatient ? {} : (patient && patient.name ? patient : (diet.patient || {}));
    const hasPatient = Boolean(patientObj.name && patientObj.name.trim() && patientObj.name !== "N/A");
    const cardHeight = 84;
    const headerHeight = 17;
    const dietDate = diet.date ? formatDate(diet.date) : formatDate(new Date());

    if (hasPatient) {
        // --- LEFT CARD: PATIENT DETAILS ---
        doc.setFillColor(...grayContentBg);
        doc.setDrawColor(...grayBorder);
        doc.roundedRect(marginX, y, cardWidth, cardHeight, 3, 3, "FD");

        doc.setFillColor(...grayHeaderBg);
        doc.rect(marginX, y, cardWidth, headerHeight, "F");

        doc.setFont("helvetica", "bold");
        doc.setFontSize(8.5);
        doc.setTextColor(...textDark);
        doc.text("PATIENT INFORMATION", marginX + 10, y + 12);

        let cardTextY = y + 28;
        doc.setFontSize(9.5);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(17, 24, 39);
        doc.text((patientObj.name || "N/A").toUpperCase(), marginX + 10, cardTextY);

        cardTextY += 13;
        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...textMuted);
        doc.text("PATIENT ID:", marginX + 10, cardTextY);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(...textDark);
        const patientIdText = patientObj.patientId || `ID: ${patientObj.id || patientObj._id || "N/A"}`;
        doc.text(patientIdText, marginX + 75, cardTextY);

        cardTextY += 12;
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...textMuted);
        doc.text("AGE / GENDER:", marginX + 10, cardTextY);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(...textDark);
        const ageGender = [
            patientObj.age ? `${patientObj.age} Yrs` : null,
            patientObj.gender || null
        ].filter(Boolean).join(" / ");
        doc.text(ageGender || "-", marginX + 75, cardTextY);

        cardTextY += 12;
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...textMuted);
        doc.text("PHONE:", marginX + 10, cardTextY);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(...textDark);
        doc.text(patientObj.phone || "-", marginX + 75, cardTextY);

        // --- RIGHT CARD: PRESCRIPTION DETAILS ---
        const rightCardX = marginX + cardWidth + cardGap;
        doc.setFillColor(...grayContentBg);
        doc.setDrawColor(...grayBorder);
        doc.roundedRect(rightCardX, y, cardWidth, cardHeight, 3, 3, "FD");

        doc.setFillColor(...grayHeaderBg);
        doc.rect(rightCardX, y, cardWidth, headerHeight, "F");

        doc.setFont("helvetica", "bold");
        doc.setFontSize(8.5);
        doc.setTextColor(...textDark);
        doc.text("PRESCRIPTION DETAILS", rightCardX + 10, y + 12);

        cardTextY = y + 28;
        doc.setFontSize(9.5);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(17, 24, 39);
        doc.text(diet.title || diet.name || "Custom Naturopathy Diet", rightCardX + 10, cardTextY);

        cardTextY += 13;
        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...textMuted);
        doc.text("DATE:", rightCardX + 10, cardTextY);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(...textDark);
        doc.text(dietDate, rightCardX + 75, cardTextY);

        cardTextY += 12;
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...textMuted);
        doc.text("CONDITION:", rightCardX + 10, cardTextY);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(...textDark);
        doc.text(diet.condition || "General Health & Wellness", rightCardX + 75, cardTextY);

        cardTextY += 12;
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...textMuted);
        doc.text("DOCTOR:", rightCardX + 10, cardTextY);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(...textDark);
        const docName = diet.doctor ? (diet.doctor.startsWith('Dr') ? diet.doctor : `Dr. ${diet.doctor}`) : "Naturopathy Consultant";
        doc.text(docName, rightCardX + 75, cardTextY);

        y += cardHeight + 14;
    } else {
        // Full width common plan card
        const fullCardWidth = pageWidth - (marginX * 2);
        const singleCardHeight = 54;
        doc.setFillColor(...grayContentBg);
        doc.setDrawColor(...grayBorder);
        doc.roundedRect(marginX, y, fullCardWidth, singleCardHeight, 3, 3, "FD");

        doc.setFillColor(...grayHeaderBg);
        doc.rect(marginX, y, fullCardWidth, headerHeight, "F");

        doc.setFont("helvetica", "bold");
        doc.setFontSize(8.5);
        doc.setTextColor(...textDark);
        doc.text("GENERAL DIET & NUTRITION PLAN", marginX + 10, y + 12);

        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(17, 24, 39);
        doc.text(diet.title || diet.name || "Standard Holistic Naturopathy Diet", marginX + 10, y + 32);

        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(...textMuted);
        doc.text(`Condition / Purpose: ${diet.condition || "General Wellness"}   |   Prescribed Date: ${dietDate}`, marginX + 10, y + 46);

        y += singleCardHeight + 14;
    }

    // ---------- Meal Schedule Table ----------
    const slots = Array.isArray(diet.slots) ? diet.slots : [];
    const tableRows = slots.map((s, idx) => [
        s.time ? `${s.time}\n(${s.title || `Slot ${idx + 1}`})` : (s.title || `Slot ${idx + 1}`),
        s.items || "-",
        s.instructions || "-"
    ]);

    autoTable(doc, {
        startY: y,
        head: [["MEAL SLOT & TIMING", "RECOMMENDED FOODS & NUTRITION", "INSTRUCTIONS & NOTES"]],
        body: tableRows.length > 0 ? tableRows : [["-", "No diet meal items specified", "-"]],
        theme: "plain",
        styles: {
            fontSize: 8.5,
            cellPadding: 6,
            textColor: textDark,
            lineColor: grayBorder,
            lineWidth: 0.5
        },
        headStyles: {
            fillColor: [229, 231, 235], // #e5e7eb
            textColor: textDark,
            fontStyle: "bold",
            fontSize: 8.5
        },
        columnStyles: {
            0: { cellWidth: 120, fontStyle: "bold", textColor: brandGreen },
            1: { cellWidth: 250 },
            2: { cellWidth: 145, textColor: textMuted }
        },
        alternateRowStyles: {
            fillColor: grayContentBg
        },
        margin: { left: marginX, right: marginX }
    });

    y = doc.lastAutoTable.finalY + 14;

    // ---------- Guidelines / Do's & Don'ts Box ----------
    const guidelines = diet.guidelines || diet.notes;
    if (guidelines && guidelines.trim()) {
        const boxWidth = pageWidth - (marginX * 2);
        const splitText = doc.splitTextToSize(guidelines.trim(), boxWidth - 24);
        const textHeight = splitText.length * 11;
        const boxHeight = textHeight + 28;

        if (y + boxHeight > pageHeight - 80) {
            doc.addPage();
            y = 40;
        }

        doc.setFillColor(...grayContentBg);
        doc.setDrawColor(...grayBorder);
        doc.roundedRect(marginX, y, boxWidth, boxHeight, 3, 3, "FD");

        doc.setFontSize(8.5);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...brandGreen);
        doc.text("LIFESTYLE GUIDELINES, DO'S & DON'TS", marginX + 12, y + 14);

        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(...textDark);
        doc.text(splitText, marginX + 12, y + 26);

        y += boxHeight + 14;
    }

    // ---------- Signatures & Footer ----------
    if (y > pageHeight - 85) {
        doc.addPage();
        y = pageHeight - 85;
    } else {
        y = Math.max(y, pageHeight - 85);
    }

    const signatoryDoc = diet.doctor ? (diet.doctor.startsWith('Dr') ? diet.doctor : `Dr. ${diet.doctor}`) : "Prescribing Physician";
    const signatoryDegree = diet.doctorDesignation ? `${diet.doctorDesignation} - Naturopathy Physician` : "Naturopathy Physician (BNYS)";

    doc.setFontSize(8.5);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...textDark);
    doc.text(signatoryDoc, pageWidth - marginX - 10, y + 10, { align: "right" });

    doc.setFontSize(7.5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...textMuted);
    doc.text(signatoryDegree, pageWidth - marginX - 10, y + 22, { align: "right" });

    // ---------- Bottom Hospital Address Banner (Identical to Bill Template) ----------
    const footerY = pageHeight - 38;
    doc.setDrawColor(...grayBorder);
    doc.setLineWidth(0.75);
    doc.line(marginX, footerY - 10, pageWidth - marginX, footerY - 10);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(55, 65, 81);
    const centreName = hospitalInfo.centreName || "GREEN NATUROPATHY";
    const addressLine = `${centreName}: ${(hospitalInfo.address || "194-C, SATHY ROAD, GANAPATHY, COIMBATORE")}, ${(hospitalInfo.city || "COIMBATORE")}, ${(hospitalInfo.state || "TAMIL NADU")} ${(hospitalInfo.pincode || "641006")}`;
    doc.text(addressLine.toUpperCase(), pageWidth / 2, footerY, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(...textMuted);
    const phoneVal = hospitalInfo.phone ? `+91 ${hospitalInfo.phone}` : "+91 9345316319";
    const emailVal = hospitalInfo.email || "greennaturopathycbe@gmail.com";
    const webVal = hospitalInfo.websiteDisplay || "greennaturopathy.com";
    doc.text(`📞 ${phoneVal}   •   ✉️ ${emailVal}   •   🌐 ${webVal}`, pageWidth / 2, footerY + 10, { align: "center" });

    return doc;
}

/**
 * Generates an HTML printable markup string for diet charts (Tamil or English)
 * styled according to the official Green Naturopathy Bill Template design system.
 */
export function generateDietHTML(diet = {}, patient = {}, language = 'en') {
    const isExplicitEmptyPatient = patient === null || patient === false || (patient && typeof patient === 'object' && Object.keys(patient).length === 0);
    const patientObj = isExplicitEmptyPatient ? {} : (patient && patient.name ? patient : (diet.patient || {}));
    const slots = Array.isArray(diet.slots) ? diet.slots : [];
    const isTamil = language === 'ta' || hasTamil(diet.title + slots.map(s => s.items).join(''));

    const dietDate = diet.date ? formatDate(diet.date) : formatDate(new Date());
    const docName = diet.doctor ? (diet.doctor.startsWith('Dr') ? diet.doctor : `Dr. ${diet.doctor}`) : "Green Naturopathy Consultant";
    const docDesignation = diet.doctorDesignation ? diet.doctorDesignation : (docName.includes('BNYS') ? '' : 'BNYS');
    const docFullText = docDesignation ? `${docName}, ${docDesignation}` : docName;
    const patientIdText = patientObj.patientId || `ID: ${patientObj.id || patientObj._id || "N/A"}`;
    const ageGender = [
        patientObj.age ? `${patientObj.age} Yrs` : null,
        patientObj.gender || null,
        patientObj.phone ? `Ph: ${patientObj.phone}` : null
    ].filter(Boolean).join("  |  ");

    const hasPatient = Boolean(patientObj.name && patientObj.name.trim() !== "");

    const headerTitle = isTamil ? "உணவு முறை மற்றும் ஊட்டச்சத்து அட்டவணை" : "Diet & Nutrition Chart";
    const conditionLabel = isTamil ? "சிகிச்சை / நிலை" : "Condition";
    const doctorLabel = isTamil ? "பரிந்துரைத்த மருத்துவர்" : "Prescribed By";
    const thTime = isTamil ? "நேரம் & உணவு வேளை" : "SLOT & TIME";
    const thItems = isTamil ? "பரிந்துரைக்கப்பட்ட உணவுகள் & ஊட்டச்சத்து" : "RECOMMENDED FOODS & NUTRITION";
    const thNotes = isTamil ? "வழிமுறைகள் / குறிப்புகள்" : "INSTRUCTIONS / NOTES";
    const guidelinesHeader = isTamil ? "பொதுவான இயற்கை மருத்துவ வழிகாட்டுதல்கள் (Guidelines & Tips)" : "LIFESTYLE GUIDELINES, DO'S & DON'TS";
    const quoteText = isTamil ? '"உணவே மருந்து, மருந்தே உணவு" — கிரீன் நேச்சுரோபதி மருத்துவமனை' : '"Let food be thy medicine and medicine be thy food." — Green Naturopathy';

    const patientPrescriptionSection = hasPatient ? `
        <!-- Two side-by-side Gray-capped cards -->
        <div style="display: flex; gap: 14px; margin-bottom: 20px;">
            <!-- LEFT CARD: Patient Details -->
            <div style="flex: 1; border: 1px solid #d1d5db; border-radius: 4px; overflow: hidden; background: #f9fafb;">
                <div style="background-color: #d1d5db; color: #1f2937; font-weight: bold; font-size: 11.5px; padding: 5px 12px; letter-spacing: 0.5px;">PATIENT DETAILS</div>
                <div style="padding: 10px 12px; font-size: 11.5px; color: #374151; line-height: 1.6;">
                    <div style="font-weight: bold; color: #111827; font-size: 12.5px; margin-bottom: 2px;">${(patientObj.name || "-").toUpperCase()}</div>
                    <div><span style="font-weight: 600; color: #6b7280;">PATIENT ID:</span> ${patientIdText}</div>
                    <div><span style="font-weight: 600; color: #6b7280;">AGE / GENDER:</span> ${ageGender || "-"}</div>
                </div>
            </div>

            <!-- RIGHT CARD: Prescription Summary -->
            <div style="flex: 1; border: 1px solid #d1d5db; border-radius: 4px; overflow: hidden; background: #f9fafb;">
                <div style="background-color: #d1d5db; color: #1f2937; font-weight: bold; font-size: 11.5px; padding: 5px 12px; letter-spacing: 0.5px;">PRESCRIPTION DETAILS</div>
                <div style="padding: 10px 12px; font-size: 11.5px; color: #374151; line-height: 1.6;">
                    <div style="font-weight: bold; color: #111827; font-size: 12px; margin-bottom: 2px;">${diet.title || diet.name || "Custom Diet Plan"}</div>
                    <div><span style="font-weight: 600; color: #6b7280;">PRESCRIBED DATE:</span> ${dietDate}</div>
                    <div><span style="font-weight: 600; color: #6b7280;">${conditionLabel.toUpperCase()}:</span> ${diet.condition || "General Wellness"}</div>
                    <div><span style="font-weight: 600; color: #6b7280;">${doctorLabel.toUpperCase()}:</span> ${docFullText}</div>
                </div>
            </div>
        </div>
    ` : `
        <!-- Full Width Common Diet Header Card -->
        <div style="border: 1px solid #d1d5db; border-radius: 4px; overflow: hidden; background: #f9fafb; margin-bottom: 20px;">
            <div style="background-color: #d1d5db; color: #1f2937; font-weight: bold; font-size: 11.5px; padding: 5px 12px; letter-spacing: 0.5px;">GENERAL DIET & NUTRITION PLAN</div>
            <div style="padding: 10px 14px; font-size: 11.5px; color: #374151; line-height: 1.6;">
                <div style="font-weight: bold; color: #111827; font-size: 13px;">${diet.title || diet.name || "Standard Holistic Naturopathy Diet"}</div>
                <div style="color: #6b7280; font-size: 11px; margin-top: 2px;">
                    ${conditionLabel}: <strong>${diet.condition || "General Health & Wellness"}</strong> &nbsp;|&nbsp; Prescribed Date: <strong>${dietDate}</strong>
                </div>
            </div>
        </div>
    `;

    return `
    <div class="diet-print-container" style="
        width: 794px;
        min-height: 1123px;
        padding: 44px 48px;
        background: #ffffff;
        color: #111827;
        font-family: 'Noto Sans Tamil', 'Nirmala UI', 'Latha', 'Tamil Sangam MN', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
    ">
        <div>
            <!-- Top Header: Title on Left, Logo on Right -->
            <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px;">
                <div>
                    <div style="font-family: 'Playfair Display', Georgia, serif; font-size: 44px; font-weight: bold; color: #4b8026; line-height: 1;">${headerTitle}</div>
                </div>
                <div>
                    <img src="${logoBase64}" style="width: 4.5cm; height: 2.26cm; object-fit: contain; display: block;" alt="Logo" />
                </div>
            </div>

            ${patientPrescriptionSection}

            <!-- Meal Schedule Table -->
            <div style="border: 1px solid #d1d5db; border-radius: 4px; overflow: hidden; margin-bottom: 22px;">
                <table style="width: 100%; border-collapse: collapse; font-size: 11.5px;">
                    <thead>
                        <tr style="background-color: #e5e7eb; color: #1f2937; font-weight: bold; border-bottom: 1px solid #d1d5db;">
                            <th style="padding: 8px 12px; text-align: left; border-right: 1px solid #d1d5db; width: 22%;">${thTime}</th>
                            <th style="padding: 8px 12px; text-align: left; border-right: 1px solid #d1d5db; width: 50%;">${thItems}</th>
                            <th style="padding: 8px 12px; text-align: left; width: 28%;">${thNotes}</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${slots.map((s, idx) => `
                            <tr style="border-bottom: 1px solid #e5e7eb; background: ${idx % 2 === 0 ? '#ffffff' : '#f9fafb'};">
                                <td style="padding: 8px 12px; border-right: 1px solid #d1d5db; font-weight: bold; color: #4b8026;">
                                    ${s.time || '-'}<br />
                                    <span style="font-size: 10px; color: #6b7280; font-weight: normal;">${s.title || `Slot ${idx + 1}`}</span>
                                </td>
                                <td style="padding: 8px 12px; border-right: 1px solid #d1d5db; color: #1f2937; white-space: pre-line;">${s.items || '-'}</td>
                                <td style="padding: 8px 12px; color: #6b7280; font-size: 10.5px;">${s.instructions || '-'}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
            </div>

            <!-- Guidelines Box -->
            ${diet.guidelines ? `
                <div style="background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 4px; padding: 12px 14px; margin-bottom: 20px;">
                    <div style="font-size: 10.5px; font-weight: bold; color: #4b8026; margin-bottom: 4px; text-transform: uppercase;">
                        ${guidelinesHeader}
                    </div>
                    <div style="font-size: 11px; color: #374151; white-space: pre-line; line-height: 1.5;">
                        ${diet.guidelines}
                    </div>
                </div>
            ` : ''}

            <!-- Signature & Quote Section -->
            <div style="display: flex; justify-content: space-between; align-items: flex-end; margin-top: 15px; margin-bottom: 15px;">
                <div style="font-size: 10.5px; font-style: italic; color: #4b8026; max-width: 450px;">
                    ${quoteText}
                </div>
                <div style="text-align: right; min-width: 200px;">
                    <div style="font-size: 12px; font-weight: bold; color: #1f2937;">${docFullText}</div>
                    <div style="font-size: 10px; color: #4b8026; font-weight: 600;">${docDesignation || 'BNYS'}</div>
                    <div style="font-size: 9px; color: #6b7280; margin-top: 1px;">Authorized Naturopathy Physician</div>
                </div>
            </div>
        </div>

        <div>
            <!-- Bottom Hospital Address Banner (Identical to Bill Template) -->
            <div style="border-top: 1px solid #d1d5db; padding-top: 8px; text-align: center;">
                <div style="font-size: 9.5px; font-weight: bold; color: #374151; letter-spacing: 0.3px; text-transform: uppercase;">
                    ${hospitalInfo.centreName || "GREEN NATUROPATHY"}: ${(hospitalInfo.address || "194-C, SATHY ROAD, GANAPATHY, COIMBATORE")}, ${(hospitalInfo.city || "COIMBATORE")}, ${(hospitalInfo.state || "TAMIL NADU")} ${(hospitalInfo.pincode || "641006")}
                </div>
                <div style="font-size: 9.5px; color: #4b5563; margin-top: 3px;">
                    📞 +91 ${hospitalInfo.phone || "9345316319"} &nbsp;&bull;&nbsp; ✉️ ${hospitalInfo.email || "greennaturopathycbe@gmail.com"} &nbsp;&bull;&nbsp; 🌐 ${hospitalInfo.websiteDisplay || "greennaturopathy.com"}
                </div>
            </div>
        </div>
    </div>
    `;
}

/**
 * Downloads a high-quality Tamil Naturopathy Diet Chart PDF.
 * Converts Tamil HTML element with exact vowel ligatures into PDF via html2canvas.
 */
export async function downloadDietPDFTamil(diet = {}, patient = {}) {
    let tamilDiet = diet;
    const slots = Array.isArray(diet.slots) ? diet.slots : [];
    const contentToTest = (diet.title || '') + slots.map(s => (s.items || '') + (s.instructions || '')).join('');

    // Translate if not already in Tamil
    if (!hasTamil(contentToTest)) {
        tamilDiet = await translateDietToTamil(diet);
    }

    const patientObj = diet.patient || patient || {};
    const hasPatient = Boolean(patientObj.name && patientObj.name.trim() !== "");
    const cleanTitle = (diet.title || diet.name || "Diet_Chart").replace(/[^a-zA-Z0-9]/g, "_");
    const safeName = (patientObj.name || "").replace(/[^a-zA-Z0-9]/g, "_");
    const safeId = (patientObj.patientId || patientObj._id || "").replace(/[^a-zA-Z0-9]/g, "_");
    const fileName = hasPatient
        ? `Diet_Chart_Tamil_${safeId ? safeId + "_" : ""}${safeName}.pdf`
        : `${cleanTitle}_Tamil_Common_Diet.pdf`;

    // Create an offscreen DOM element
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.left = '-9999px';
    container.style.top = '0';
    container.style.zIndex = '-9999';
    container.innerHTML = generateDietHTML(tamilDiet, patientObj, 'ta');
    document.body.appendChild(container);

    try {
        const targetElement = container.querySelector('.diet-print-container') || container;
        const canvas = await html2canvas(targetElement, {
            scale: 2, // 300 DPI high resolution
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff'
        });

        const imgData = canvas.toDataURL('image/jpeg', 0.96);
        const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

        pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
        pdf.save(fileName);
    } finally {
        document.body.removeChild(container);
    }
}

/**
 * Primary Download Diet PDF function.
 * @param {Object} diet - Diet object
 * @param {Object} patient - Patient object
 * @param {string} language - 'en' for English, 'ta' for Tamil
 */
export async function downloadDietPDF(diet = {}, patient = {}, language = 'en') {
    if (language === 'ta') {
        return downloadDietPDFTamil(diet, patient);
    }

    const doc = buildDietPDFDoc(diet, patient);
    const patientObj = diet.patient || patient || {};
    const hasPatient = Boolean(patientObj.name && patientObj.name.trim() !== "");
    const cleanTitle = (diet.title || diet.name || "Diet_Chart").replace(/[^a-zA-Z0-9]/g, "_");
    const safeName = (patientObj.name || "").replace(/[^a-zA-Z0-9]/g, "_");
    const safeId = (patientObj.patientId || patientObj._id || "").replace(/[^a-zA-Z0-9]/g, "_");
    const fileName = hasPatient
        ? `Diet_Chart_${safeId ? safeId + "_" : ""}${safeName}.pdf`
        : `${cleanTitle}_Common_Diet.pdf`;
    doc.save(fileName);
}

/**
 * Opens a print dialog for instant printing or browser "Save as PDF".
 */
export async function printDiet(diet = {}, patient = {}, language = 'en') {
    let dietToPrint = diet;
    if (language === 'ta') {
        const slots = Array.isArray(diet.slots) ? diet.slots : [];
        const contentToTest = (diet.title || '') + slots.map(s => (s.items || '') + (s.instructions || '')).join('');
        if (!hasTamil(contentToTest)) {
            dietToPrint = await translateDietToTamil(diet);
        }
    }

    const patientObj = diet.patient || patient || {};
    const htmlContent = generateDietHTML(dietToPrint, patientObj, language);

    const printWindow = window.open('', '_blank', 'width=880,height=900');
    if (!printWindow) {
        alert('Please allow popups to print diet chart.');
        return;
    }

    printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
            <title>Diet Chart - ${patientObj.name || 'Patient'}</title>
            <style>
                @page { size: A4; margin: 0; }
                body { margin: 0; padding: 0; background: #f3f4f6; }
                @media print {
                    body { background: #ffffff; }
                    .no-print { display: none !important; }
                }
            </style>
        </head>
        <body>
            <div style="display: flex; justify-content: center; padding: 20px 0;">
                ${htmlContent}
            </div>
            <script>
                window.onload = function() {
                    window.print();
                    setTimeout(function() { window.close(); }, 500);
                };
            </script>
        </body>
        </html>
    `);
    printWindow.document.close();
}
